import { OAuthProvider } from '#modules/user/features/auth/constants/providers';
import type { StatsVisibilityInput } from '../../../model/profile';

/**
 * Что вообще можно настроить у каждого аккаунта.
 *
 * Раньше настройки перечисляли одни и те же аккаунты трижды - «привязанные аккаунты», «видимость
 * аккаунтов» и «игровая статистика», - и человек не мог собрать в голове, что относится к его
 * Steam: приходилось листать три раздела и держать в уме, где какой тумблер. Теперь провайдер один
 * раз, а внутри него столько строк, сколько он на самом деле даёт.
 *
 * Оттого строки и заданы таблицей, а не общим набором с исключениями: у Google нет ни ника, ни
 * статистики, ни присутствия - он способ войти, а не игровой аккаунт, и пустых тумблеров у него
 * быть не должно. У Xbox рангов и K/D нет (вендор Apex ищет только по геймертегу, который Microsoft
 * переиспользует между людьми), зато есть свои игры, часы и «играет сейчас» - их и прячет тумблер.
 */
export type AccountRow = 'nickname' | 'stats' | 'discordPresence';

type AccountCapabilities = {
  rows: AccountRow[];
  /** Поле мутации StatsVisibility, если у провайдера есть тумблер статистики. */
  statsInputKey?: keyof StatsVisibilityInput;
  /** Ключ в карте stats_visibility, по которому гейтится чтение. */
  statsFlag?: string;
};

/**
 * Таблица полная, а не частичная, и это защита: новый провайдер в схеме перестанет
 * компилироваться, пока про него здесь не скажут, что он умеет. У частичной карты он собрался бы
 * молча и просто не появился бы в настройках - ни посмотреть, ни привязать.
 */
export const ACCOUNT_CAPABILITIES: Record<OAuthProvider, AccountCapabilities> = {
  [OAuthProvider.STEAM]: {
    rows: ['nickname', 'stats'],
    statsInputKey: 'steam',
    statsFlag: 'STEAM',
  },
  [OAuthProvider.RIOT_GAMES]: {
    rows: ['nickname', 'stats'],
    statsInputKey: 'riotGames',
    statsFlag: 'RIOT_GAMES',
  },
  [OAuthProvider.EPIC_GAMES]: {
    rows: ['nickname', 'stats'],
    statsInputKey: 'epicGames',
    statsFlag: 'EPIC_GAMES',
  },
  [OAuthProvider.BUNGIE]: {
    rows: ['nickname', 'stats'],
    statsInputKey: 'bungie',
    statsFlag: 'BUNGIE',
  },
  // Discord статистики не даёт вовсе: от него приходит только «во что играет сейчас».
  [OAuthProvider.DISCORD]: { rows: ['nickname', 'discordPresence'] },
  // Xbox: библиотека, часы и текущая игра. Рангов нет - см. комментарий выше.
  [OAuthProvider.XBOX]: {
    rows: ['nickname', 'stats'],
    statsInputKey: 'xbox',
    statsFlag: 'XBOX',
  },
  // Google - способ войти. Ни ника, ни цифр, ни присутствия.
  [OAuthProvider.GOOGLE]: { rows: [] },
  [OAuthProvider.APPLE]: { rows: [] },
};

/**
 * Порядок карточек: сначала то, что даёт игровые данные, потом способы войти.
 *
 * Список собирается из самой таблицы, а не выписывается вторым перечнем: иначе провайдер, забытый
 * здесь, снова пропал бы из настроек молча. Незнакомый уходит в конец, а не теряется.
 */
const PRIORITY: OAuthProvider[] = [
  OAuthProvider.STEAM,
  OAuthProvider.DISCORD,
  OAuthProvider.RIOT_GAMES,
  OAuthProvider.EPIC_GAMES,
  OAuthProvider.BUNGIE,
  OAuthProvider.XBOX,
  OAuthProvider.GOOGLE,
  OAuthProvider.APPLE,
];

export const ACCOUNT_ORDER: OAuthProvider[] = (
  Object.keys(ACCOUNT_CAPABILITIES) as OAuthProvider[]
).toSorted((left, right) => {
  const leftIndex = PRIORITY.indexOf(left);
  const rightIndex = PRIORITY.indexOf(right);
  return (
    (leftIndex === -1 ? PRIORITY.length : leftIndex) -
    (rightIndex === -1 ? PRIORITY.length : rightIndex)
  );
});

/**
 * Игровые аккаунты и способы войти - разные вещи, и на экране это разные блоки.
 *
 * Google не даёт ни ника, ни цифр, ни присутствия: в одном ряду с игровыми он выглядел так же и
 * обещал то же самое, а внутри у него пусто. Деление считается по таблице возможностей, а не
 * выписывается третьим перечнем, чтобы новый провайдер не оказался молча ни в одном блоке.
 */
export const GAME_ACCOUNTS = ACCOUNT_ORDER.filter(
  (provider) => ACCOUNT_CAPABILITIES[provider].rows.length > 0
);

/**
 * Способы войти: у этих провайдеров есть кнопка на экране входа.
 *
 * Раньше «способом входа» называлась привязка без статистики, то есть один Google, а Discord,
 * Steam, Epic и Riot лежали среди игровых аккаунтов, хотя войти можно и через них. Теперь список
 * отвечает на свой вопрос прямо: сюда попадает то, через что человек может попасть в аккаунт.
 *
 * Тот же набор держит сервер, когда не даёт отвязать последний способ войти
 * (`OAuthService.SIGN_IN_TYPES` в teamplay-user): Xbox и Bungie только привязываются.
 *
 * Порядок по тому, кто сколько людей впускает: Discord и Google примерно поровну (около восьми
 * тысяч каждый), дальше Steam, Epic, Riot.
 */
export const SIGN_IN_ACCOUNTS: OAuthProvider[] = [
  OAuthProvider.DISCORD,
  OAuthProvider.GOOGLE,
  OAuthProvider.STEAM,
  OAuthProvider.EPIC_GAMES,
  OAuthProvider.RIOT_GAMES,
  OAuthProvider.APPLE,
];

export const isSignInProvider = (provider: OAuthProvider) => SIGN_IN_ACCOUNTS.includes(provider);

/**
 * Якорь карточки аккаунта. Один помощник на обе стороны: тот, кто ставит id, и тот, кто строит
 * ссылку. Расходиться им нельзя - ссылка молча перестанет никуда вести.
 */
export const accountCardId = (provider: OAuthProvider) => `account-${provider.toLowerCase()}`;
